(() => {
  'use strict';

  if (
    window.shopifyPurpleEventTrackingInitialized ||
    window.shopifyEngagementDataLayerInitialized ||
    window.customDataLayerTrackingInitialized
  ) {
    console.warn('[Engagement] Another tracker is already loaded. Keep only one global tracking file.');
    return;
  }

  window.shopifyPurpleEventTrackingInitialized = true;
  window.customDataLayerTrackingInitialized = true;
  window.shopifyEngagementDataLayerInitialized = true;

  const initializeTracking = () => {
    const context = window.customDataLayerContext || {};
    const pageType = context.page_type || document.body.dataset.template || 'other';

    window.dataLayer = window.dataLayer || [];

    const pushEvent = (event, parameters = {}) => {
      window.dataLayer.push({
        ...parameters,
        event,
        tracking_source: 'shopify_engagement_custom',
        tracking_version: '2026-10-23-js',
      });
    };

    const timerThresholds = [
      { seconds: 30, event: 'timer_30s' },
    ];
    const firedTimers = new Set();
    let activeMilliseconds = 0;
    let lastTick = performance.now();
    let wasVisible = document.visibilityState === 'visible';
    let pageSuspended = false;

    const updateActiveTime = () => {
      const now = performance.now();

      if (wasVisible) {
        activeMilliseconds += now - lastTick;
      }

      lastTick = now;
      wasVisible = !pageSuspended && document.visibilityState === 'visible';

      timerThresholds.forEach((threshold) => {
        if (activeMilliseconds < threshold.seconds * 1000 || firedTimers.has(threshold.event)) return;

        firedTimers.add(threshold.event);
        pushEvent(threshold.event, {
          active_time: threshold.seconds,
          page_type: pageType,
        });
      });

      if (firedTimers.size === timerThresholds.length) {
        window.clearInterval(activeTimer);
        document.removeEventListener('visibilitychange', updateActiveTime);
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
      }
    };

    const handlePageHide = () => {
      updateActiveTime();
      pageSuspended = true;
      wasVisible = false;
    };

    const handlePageShow = () => {
      if (!pageSuspended) return;
      pageSuspended = false;
      lastTick = performance.now();
      wasVisible = document.visibilityState === 'visible';
    };

    const activeTimer = window.setInterval(updateActiveTime, 250);
    document.addEventListener('visibilitychange', updateActiveTime);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    const scrollThresholds = [
      { percent: 75, event: 'page_scroll_75' },
    ];
    const firedScrollEvents = new Set();
    let scrollFramePending = false;

    const checkScrollDepth = () => {
      scrollFramePending = false;

      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight
      );
      const scrollableHeight = documentHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const scrollDepth = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));

      scrollThresholds.forEach((threshold) => {
        if (scrollDepth < threshold.percent || firedScrollEvents.has(threshold.event)) return;

        firedScrollEvents.add(threshold.event);
        pushEvent(threshold.event, {
          scroll_depth: threshold.percent,
          page_type: pageType,
        });
      });
    };

    window.addEventListener(
      'scroll',
      () => {
        if (scrollFramePending) return;
        scrollFramePending = true;
        window.requestAnimationFrame(checkScrollDepth);
      },
      { passive: true }
    );

    const videoStates = new WeakMap();

    const getVideoSource = (video) => {
      const source = video.querySelector('source[src]');
      return video.currentSrc || video.src || (source ? source.src : '');
    };

    const getVideoState = (video) => {
      const source = getVideoSource(video);
      let state = videoStates.get(video);

      if (!state || state.source !== source) {
        const pendingIntent = state && state.manualUntil > performance.now();
        state = {
          source,
          events: new Set(),
          playType: '',
          userInteracted: Boolean(pendingIntent),
          manualUntil: pendingIntent ? state.manualUntil : 0,
          interactionAt: pendingIntent ? state.interactionAt : -Infinity,
          lastTime: null,
          lastAt: 0,
          lastPlaying: false,
          seeking: false,
        };
        videoStates.set(video, state);
      }
      return state;
    };

    const getElementPosition = (element) => {
      if (element.dataset.analyticsPosition) return element.dataset.analyticsPosition;

      const container = element.closest('[data-analytics-position], section');
      if (!container) return 'unknown';

      return container.dataset.analyticsPosition || container.id || 'section';
    };

    const getVideoName = (video) => {
      if (video.dataset.videoName) return video.dataset.videoName;

      const source = getVideoSource(video);
      if (!source) return 'unnamed_video';

      try {
        return decodeURIComponent(new URL(source, window.location.href).pathname.split('/').pop()) || 'unnamed_video';
      } catch (error) {
        return source;
      }
    };

    const getVideoParameters = (video) => {
      const state = getVideoState(video);
      return {
        video_name: getVideoName(video),
        video_type: video.dataset.videoType || 'html5',
        video_position: getElementPosition(video),
        video_duration: Number.isFinite(video.duration) ? Math.round(video.duration) : 0,
        video_play_type: state.playType || 'unknown',
        video_autoplay: video.autoplay,
        video_user_interacted: state.userInteracted,
        page_type: pageType,
      };
    };

    const pushVideoEventOnce = (video, event) => {
      const state = getVideoState(video);
      if (state.events.has(event)) return;

      state.events.add(event);
      pushEvent(event, getVideoParameters(video));
    };

    const recordVideoIntent = (event) => {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      if (event.type === 'keydown' && ![' ', 'Enter', 'k', 'K'].includes(event.key)) return;

      const control = event.target.closest('[data-analytics-video-play], [aria-controls]');
      let video = event.target.closest('video');

      if (!video && control) {
        const ids = control.getAttribute('data-analytics-video-play') || control.getAttribute('aria-controls') || '';
        for (const id of ids.trim().split(/\s+/)) {
          const candidate = document.getElementById(id);
          if (candidate instanceof HTMLVideoElement) {
            video = candidate;
            break;
          }
        }
      }
      if (!video) return;

      const state = getVideoState(video);
      state.userInteracted = true;
      state.interactionAt = performance.now();
      if (video.paused) state.manualUntil = performance.now() + 2000;
    };

    const startVideo = (video) => {
      const state = getVideoState(video);
      if (!state.playType) {
        const declaredType = video.dataset.videoPlayType;
        // Native controls can consume pointer events inside the browser shadow tree.
        const nativeIntent = declaredType !== 'autoplay' && video.controls && navigator.userActivation?.isActive &&
          (video.matches(':hover') || document.activeElement === video);
        if (nativeIntent) {
          state.userInteracted = true;
          state.interactionAt = performance.now();
        }
        state.playType = state.manualUntil > performance.now() || nativeIntent ? 'manual' :
          (['manual', 'autoplay', 'unknown'].includes(declaredType) ? declaredType :
            (video.autoplay ? 'autoplay' : 'unknown'));
      }
      pushVideoEventOnce(video, 'video_start');
    };

    const checkVideoLoop = (video, state) => {
      if (!video.loop || !state.events.has('video_start') ||
        !Number.isFinite(video.duration) || video.duration <= 0 ||
        state.lastTime === null || !state.lastPlaying || video.playbackRate <= 0) return;

      // Require a near-end wrap with enough elapsed playback time, not any backwards seek.
      const edge = Math.min(0.5, video.duration * 0.05);
      const elapsed = (performance.now() - state.lastAt) / 1000;
      const remaining = (video.duration - state.lastTime) / video.playbackRate;
      if (state.lastTime >= video.duration - edge &&
        video.currentTime < edge && video.currentTime < state.lastTime &&
        elapsed >= remaining - 0.05 &&
        performance.now() - state.interactionAt > 1000) {
        pushVideoEventOnce(video, 'video_complete');
      }
    };

    const checkVideoProgress = (video, ended = false) => {
      const state = getVideoState(video);
      if (!state.events.has('video_start') || state.seeking || video.seeking ||
        (!ended && video.paused) || !Number.isFinite(video.duration) || video.duration <= 0) return;

      const currentTime = video.currentTime;
      const progress = (currentTime / video.duration) * 100;

      if (progress >= 50) pushVideoEventOnce(video, 'video_50');
      if (progress >= 75) pushVideoEventOnce(video, 'video_75');
      checkVideoLoop(video, state);

      state.lastTime = currentTime;
      state.lastAt = performance.now();
      state.lastPlaying = !video.paused;
    };

    document.addEventListener('pointerdown', recordVideoIntent, true);
    document.addEventListener('keydown', recordVideoIntent, true);
    document.addEventListener('click', recordVideoIntent, true);

    document.addEventListener(
      'play',
      (event) => {
        if (event.target instanceof HTMLVideoElement) startVideo(event.target);
      },
      true
    );

    document.addEventListener(
      'timeupdate',
      (event) => {
        if (event.target instanceof HTMLVideoElement) checkVideoProgress(event.target);
      },
      true
    );

    document.addEventListener(
      'ended',
      (event) => {
        if (!(event.target instanceof HTMLVideoElement)) return;
        if (!getVideoState(event.target).events.has('video_start')) return;

        checkVideoProgress(event.target, true);
        pushVideoEventOnce(event.target, 'video_complete');
      },
      true
    );

    document.addEventListener('seeking', (event) => {
      if (event.target instanceof HTMLVideoElement) getVideoState(event.target).seeking = true;
    }, true);

    document.addEventListener('seeked', (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;

      const state = getVideoState(video);
      checkVideoLoop(video, state);
      state.seeking = false;
      state.lastTime = video.currentTime;
      state.lastAt = performance.now();
      state.lastPlaying = !video.paused;
    }, true);

    document.querySelectorAll('video').forEach((video) => {
      if (!video.paused) startVideo(video);
    });

    const ctaEvents = new Map([
      ['learn_more_click', 'learn_more_click'],
      ['view_details_click', 'view_details_click'],
      ['view_detail_click', 'view_details_click'],
    ]);

    const isCartSubmit = (element) => {
      const button = element.closest('button, input[type="submit"]');
      const form = button && button.form;
      if (!form || button.type !== 'submit') return false;

      const path = new URL(form.action, window.location.href).pathname;
      return form.getAttribute('is') === 'product-form' || /\/cart\/add(?:\.js)?\/?$/.test(path);
    };

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const explicit = event.target.closest('[data-datalayer-event]');
      const trigger = explicit || event.target.closest('a[href], button, [role="button"]');
      if (!trigger) return;

      let eventName = ctaEvents.get(trigger.dataset.datalayerEvent);
      if (!explicit) {
        const label = (trigger.getAttribute('aria-label') || trigger.textContent || '')
          .trim().replace(/\s+/g, ' ').replace(/[>\u203a\u00bb]+$/g, '').trim().toLowerCase();
        if (label === 'view details' || label === 'view detail') eventName = 'view_details_click';
      }
      if (!eventName) return;
      if (eventName === 'view_details_click' && isCartSubmit(event.target)) return;

      const link = trigger.closest('a[href]');
      const destination = eventName === 'view_details_click' && link ?
        link.href : trigger.dataset.destinationUrl || trigger.href || '';

      pushEvent(eventName, {
        cta_name: trigger.dataset.ctaName || trigger.textContent.trim(),
        cta_position: trigger.dataset.ctaPosition || getElementPosition(trigger),
        cta_type: trigger.dataset.ctaType || trigger.tagName.toLowerCase(),
        page_type: pageType,
        destination_url: destination,
      });
    }, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTracking, { once: true });
  } else {
    initializeTracking();
  }
})();
